import {
  Tabs as UiTabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@Pages/pageRuntime/components/ui/Tabs';
import { cn } from '@Pages/pageRuntime/lib/utils';
import type React from 'react';

type TabsVariant = 'pill' | 'underline' | 'rail';

type Tab = {
  /**
   * Tab identifier used by `value`/`defaultValue`.
   */
  value: string;

  /**
   * Tab trigger label.
   */
  label: React.ReactNode;

  /**
   * Tab panel content.
   */
  content: React.ReactNode;
};

type TabsProps = {
  /**
   * Tab definitions.
   */
  tabs: Tab[];

  /**
   * Initially selected tab for uncontrolled usage.
   */
  defaultValue?: string;

  /**
   * Controlled active tab value.
   */
  value?: string;

  /**
   * Called when the active tab changes.
   */
  onValueChange?: (value: string) => void;

  /**
   * Visual style. Defaults to `pill`.
   */
  variant?: TabsVariant;

  className?: string;
  listClassName?: string;
  contentClassName?: string;
};

/**
 * A responsive tab interface for organizing related sections of content. Define larger
 * JSX content nodes before the tabs array and reference those variables from each tab
 * object.
 *
 * @example
 * <Sdk.Components.Tabs
 *   defaultValue="overview"
 *   tabs={[{ value: 'overview', label: 'Overview', content: <div>Overview</div> }]}
 * />
 */
export function Tabs({
  tabs,
  defaultValue,
  value,
  onValueChange,
  variant = 'pill',
  className,
  listClassName,
  contentClassName
}: TabsProps) {
  const resolvedDefaultValue = defaultValue ?? tabs[0]?.value;

  if (tabs.length === 0) {
    return null;
  }

  return (
    <UiTabs
      defaultValue={value ? undefined : resolvedDefaultValue}
      value={value}
      onValueChange={onValueChange}
      variant={variant}
      className={cn(variant === 'rail' && 'sm:flex sm:min-h-[280px]', className)}
    >
      <TabsList className={listClassName}>
        {tabs.map((tab) => (
          <TabsTrigger key={tab.value} value={tab.value}>
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>
      <div className={cn(variant === 'rail' && 'min-w-0 flex-1 sm:pl-6')}>
        {tabs.map((tab) => (
          <TabsContent key={tab.value} value={tab.value} className={contentClassName}>
            {tab.content}
          </TabsContent>
        ))}
      </div>
    </UiTabs>
  );
}
